import { Graphics, Sprite, Text } from 'pixi.js';
import type { Application, FederatedPointerEvent } from 'pixi.js';
import { Scene } from '../core/Scene';
import type { SceneManager } from '../core/SceneManager';
import { GAME_WIDTH, GAME_HEIGHT } from '../constants';
import { SegmentTerrain } from '../core/Terrain';
import type { Segment } from '../core/Terrain';
import {
  createBallSprite,
  createTeeSprite,
  TEE_HEIGHT,
  BALL_RADIUS,
  HOLE_WIDTH,
  HOLE_CAPTURE_SPEED,
} from '../core/GameObjects';
import {
  beginAim,
  updateAim,
  isInCancelZone,
  getLaunchVelocity,
  CANCEL_RADIUS,
  MAX_DRAG_DISTANCE,
  type AimState,
} from '../core/AimInput';
import { vec2, lengthSq } from '../core/Vec2';
import type { Vec2 } from '../core/Vec2';
import { createBall, step, simulate, PHYSICS, type BallState } from '../core/Physics';
import { isInHole } from '../core/HoleDetection';
import { generateLevel, segmentsIntersect } from '../core/LevelGenerator';

type GamePhase = 'aiming' | 'flight' | 'scored' | 'missed';

const TRAJECTORY_MAX_STEPS = 600;
const SCORED_PAUSE = 1.0;
const MISSED_PAUSE = 0.5;

export class PlayingScene extends Scene {
  private app: Application;
  private terrain!: SegmentTerrain;
  private ball!: Sprite;
  private ballWrapX!: Sprite;
  private ballWrapY!: Sprite;
  private tee!: Sprite;
  private aimGraphics!: Graphics;
  private offScreenArrow!: Graphics;
  private aimState: AimState | null = null;

  private phase: GamePhase = 'aiming';
  private ballState: BallState | null = null;
  private strokes = 0;
  private phaseTimer = 0;
  private timeAccum = 0;

  private teePos!: Vec2;
  private holeX!: number;
  private holeSurfaceY!: number;
  private holeDepth!: number;
  private currentSeed = 1;

  // Debug: dynamic connection stepping
  private sortedPieces: { left: Vec2; right: Vec2; isCup: boolean }[] = [];
  private currentDeparture: Vec2 = vec2(0, 0);
  private connectionIndex = 0;
  private totalConnections = 0;
  private debugGraphics!: Graphics;
  private trajectoryPoints: Vec2[] = [];
  // Track drawn connections for re-simulation
  private drawnConnections: { segs: Segment[]; isCupConn: boolean; isBlue: boolean }[] = [];
  private structuralSegments: Segment[] = [];
  private levelPassed = false;
  private solutionVx = 0;
  private solutionVy = 0;
  // Retry tracking: when a level fails, we regenerate with same tee but different seed
  private retryCount = 0;
  private fixedTeePos: { x: number; y: number } | undefined = undefined;
  // Debug mode toggle
  private debugMode = false;
  private trajectoryGfx!: Graphics;

  private onKeyDown = (e: KeyboardEvent): void => {
    if ((e.key === 'd' || e.key === 'D') && import.meta.env.DEV) {
      this.debugMode = !this.debugMode;
      console.log(`Debug mode: ${this.debugMode ? 'ON' : 'OFF'}`);
      this.loadLevel(this.currentSeed);
      return;
    }

    if (e.key !== ' ') return;

    if (this.debugMode) {
      this.handleDebugSpacebar();
    } else {
      this.handleNormalSpacebar();
    }
  };

  /** Normal mode spacebar: fire solution shot as auto hole-in-one. */
  private handleNormalSpacebar(): void {
    if (this.phase !== 'aiming') return;
    this.ballState = createBall(this.ball.x, this.ball.y, this.solutionVx, this.solutionVy);
    this.phase = 'flight';
    this.timeAccum = 0;
    this.strokes++;
    console.log(`Auto shot: launch (${this.solutionVx.toFixed(1)}, ${this.solutionVy.toFixed(1)})`);
  }

  /** Debug mode spacebar: step through connections one at a time. */
  private handleDebugSpacebar(): void {
    if (this.phase !== 'aiming') return;

    if (this.connectionIndex < this.totalConnections) {
      const pieces = this.sortedPieces;
      const srcIdx = this.connectionIndex;
      const nextIdx = (this.connectionIndex + 1) % pieces.length;
      const isWrap = this.connectionIndex === this.totalConnections - 1;
      const srcPiece = pieces[srcIdx]!;
      const nextPiece = pieces[nextIdx]!;
      const touchesCup = srcPiece.isCup || nextPiece.isCup;

      // Default: connect to next piece's left endpoint
      const defaultSegs = this.makeConnectionSegments(this.currentDeparture, nextPiece.left, isWrap);
      const defaultIntersects = this.connectionIntersectsTrajectory(defaultSegs);

      if (defaultIntersects && !nextPiece.isCup) {
        // Try alternate: connect to next piece's right endpoint
        const altSegs = this.makeConnectionSegments(this.currentDeparture, nextPiece.right, isWrap);
        const altIntersects = this.connectionIntersectsTrajectory(altSegs);

        if (!altIntersects) {
          // Alternate is clear — draw green, depart from opposite (left)
          this.drawConnectionSegments(altSegs, 0x00ff00);
          this.drawnConnections.push({ segs: altSegs, isCupConn: touchesCup, isBlue: false });
          this.currentDeparture = nextPiece.left;
          console.log(`Connection ${this.connectionIndex + 1}/${this.totalConnections} drawn (flipped, clear)`);
        } else {
          // Both intersect — draw original in blue, depart from right
          this.drawConnectionSegments(defaultSegs, 0x000088);
          this.drawnConnections.push({ segs: defaultSegs, isCupConn: touchesCup, isBlue: true });
          this.currentDeparture = nextPiece.right;
          console.log(`Connection ${this.connectionIndex + 1}/${this.totalConnections} drawn (both intersect)`);
        }
      } else if (defaultIntersects) {
        // Intersects but it's the cup — draw blue, depart from right
        this.drawConnectionSegments(defaultSegs, 0x000088);
        this.drawnConnections.push({ segs: defaultSegs, isCupConn: true, isBlue: true });
        this.currentDeparture = nextPiece.right;
        console.log(`Connection ${this.connectionIndex + 1}/${this.totalConnections} drawn (cup, INTERSECTS)`);
      } else {
        // No intersection — draw green, depart from right
        this.drawConnectionSegments(defaultSegs, 0x00ff00);
        this.drawnConnections.push({ segs: defaultSegs, isCupConn: touchesCup, isBlue: false });
        this.currentDeparture = nextPiece.right;
        console.log(`Connection ${this.connectionIndex + 1}/${this.totalConnections} drawn (clear)`);
      }

      this.connectionIndex++;

      // After all connections drawn, check if we can re-simulate
      if (this.connectionIndex === this.totalConnections) {
        this.tryResimulateWithConnections();
      }
    } else {
      if (this.levelPassed) {
        // All connections clear — advance to next level
        this.currentSeed++;
        this.retryCount = 0;
        this.fixedTeePos = undefined;
        this.strokes = 0;
        this.loadLevel(this.currentSeed);
        console.log(`Loading level ${this.currentSeed}`);
      } else {
        // Level failed — regenerate with same tee but different seed
        this.retryCount++;
        this.strokes = 0;
        this.loadLevel(this.currentSeed);
        console.log(`Regenerating level ${this.currentSeed} (retry ${this.retryCount})`);
      }
    }
  }

  constructor(sceneManager: SceneManager, app: Application) {
    super();
    void sceneManager;
    this.app = app;

    this.loadLevel(this.currentSeed);

    this.container.eventMode = 'static';
    this.container.on('pointerdown', this.onPointerDown, this);
    this.container.on('pointermove', this.onPointerMove, this);
    this.container.on('pointerup', this.onPointerUp, this);
    this.container.on('pointerupoutside', this.onPointerUp, this);

    window.addEventListener('keydown', this.onKeyDown);
  }

  private loadLevel(seed: number): void {
    // Clear existing children
    this.container.removeChildren();

    // On retry, use a modified seed but keep the tee position
    const effectiveSeed = seed + this.retryCount * 77777;
    let levelData = generateLevel(effectiveSeed, this.fixedTeePos);

    // Store tee position for potential retries
    if (!this.fixedTeePos) {
      this.fixedTeePos = { x: levelData.teeX, y: levelData.teeSurfaceY };
    }

    // In normal mode, verify the solution shot still works with full terrain
    // (connecting segments may deflect the ball). Auto-retry if it fails.
    if (!this.debugMode) {
      const MAX_RESIM_RETRIES = 50;
      for (let r = 0; r < MAX_RESIM_RETRIES; r++) {
        const fullTerrain = new SegmentTerrain(levelData.segments);
        const ballStartY = levelData.teeSurfaceY - TEE_HEIGHT - BALL_RADIUS;
        const ball = createBall(levelData.teeX, ballStartY, levelData.solutionVx, levelData.solutionVy);
        let scored = false;
        for (let i = 0; i < 20000; i++) {
          step(ball, fullTerrain);
          const speed = Math.sqrt(lengthSq(ball.vel));
          if (isInHole(ball.pos.x, ball.pos.y, speed, levelData.holeX, levelData.holeSurfaceY, HOLE_WIDTH, levelData.holeDepth, HOLE_CAPTURE_SPEED)) {
            scored = true;
            break;
          }
          if (ball.atRest) break;
          if (ball.pos.y > GAME_HEIGHT + 200) break;
        }
        if (scored) break;
        // Re-simulation failed — retry with different seed, same tee
        this.retryCount++;
        const retrySeed = seed + this.retryCount * 77777;
        console.log(`Level ${seed}: re-simulation failed (retry ${this.retryCount}), regenerating...`);
        levelData = generateLevel(retrySeed, this.fixedTeePos);
      }
    }

    // Store trajectory for debug intersection checking, trimming the tail
    // where the ball is settling inside the hole (so connections to the cup
    // don't falsely intersect the in-hole trajectory)
    const rawTraj = levelData.trajectory;
    const holeHalfW = HOLE_WIDTH / 2;
    let trimEnd = rawTraj.length;
    for (let i = rawTraj.length - 1; i >= 0; i--) {
      const p = rawTraj[i]!;
      const inHoleX = p.x >= levelData.holeX - holeHalfW && p.x <= levelData.holeX + holeHalfW;
      const inHoleY = p.y >= levelData.holeSurfaceY && p.y <= levelData.holeSurfaceY + levelData.holeDepth;
      if (inHoleX && inHoleY) {
        trimEnd = i;
      } else {
        break;
      }
    }
    this.trajectoryPoints = rawTraj.slice(0, trimEnd);

    // Build sorted pieces for dynamic connection stepping
    const pieces: { left: Vec2; right: Vec2; isCup: boolean }[] = [];
    const pad = levelData.segments[0]!;
    pieces.push(pad.a.x <= pad.b.x
      ? { left: pad.a, right: pad.b, isCup: false }
      : { left: pad.b, right: pad.a, isCup: false });
    for (let i = 1; i <= levelData.numEdges; i++) {
      const edge = levelData.segments[i]!;
      pieces.push(edge.a.x <= edge.b.x
        ? { left: edge.a, right: edge.b, isCup: false }
        : { left: edge.b, right: edge.a, isCup: false });
    }
    const cupStart = 1 + levelData.numEdges;
    const cupLW = levelData.segments[cupStart + 1]!;
    const cupRW = levelData.segments[cupStart + 2]!;
    pieces.push(cupLW.a.x <= cupRW.a.x
      ? { left: cupLW.a, right: cupRW.a, isCup: true }
      : { left: cupRW.a, right: cupLW.a, isCup: true });
    pieces.sort((a, b) => a.left.x - b.left.x);

    this.sortedPieces = pieces;
    this.currentDeparture = pieces[0]!.right;
    this.connectionIndex = 0;
    this.totalConnections = pieces.length; // N pieces = N connections (including wrap)
    this.drawnConnections = [];
    this.solutionVx = levelData.solutionVx;
    this.solutionVy = levelData.solutionVy;
    this.levelPassed = false;

    // Sky background
    const sky = new Graphics();
    sky.rect(0, 0, GAME_WIDTH, GAME_HEIGHT);
    sky.fill(0x87ceeb);
    this.container.addChild(sky);

    // Build terrain: in debug mode use structural only, in normal mode use all segments
    const structuralCount = 1 + levelData.numEdges + 3; // pad + edges + cup
    const structuralSegments = levelData.segments.slice(0, structuralCount);
    this.structuralSegments = structuralSegments;

    if (this.debugMode) {
      this.terrain = new SegmentTerrain(structuralSegments);
    } else {
      this.terrain = new SegmentTerrain(levelData.segments);
    }

    // Set hole position and dimensions (per-level)
    this.holeX = levelData.holeX;
    this.holeSurfaceY = levelData.holeSurfaceY;
    this.holeDepth = levelData.holeDepth;

    // Draw black fill behind the cup, matching asymmetric wall heights
    const halfW = HOLE_WIDTH / 2;
    const cupLeftWallIdx = 1 + levelData.numEdges + 1;
    const cupRightWallIdx = 1 + levelData.numEdges + 2;
    const leftWall = levelData.segments[cupLeftWallIdx]!;
    const rightWall = levelData.segments[cupRightWallIdx]!;
    const bottomY = this.holeSurfaceY + this.holeDepth;
    const cupFill = new Graphics();
    cupFill.moveTo(this.holeX - halfW, leftWall.a.y);
    cupFill.lineTo(this.holeX - halfW, bottomY);
    cupFill.lineTo(this.holeX + halfW, bottomY);
    cupFill.lineTo(this.holeX + halfW, rightWall.a.y);
    cupFill.closePath();
    cupFill.fill(0x000000);
    this.container.addChild(cupFill);

    // Draw trajectory in red (debug mode only)
    this.trajectoryGfx = new Graphics();
    if (this.debugMode) {
      const traj = levelData.trajectory;
      const wrapThreshold = GAME_WIDTH / 2;
      if (traj.length > 1) {
        for (let i = 0; i < traj.length - 1; i++) {
          const ax = traj[i]!.x, ay = traj[i]!.y;
          const bx = traj[i + 1]!.x, by = traj[i + 1]!.y;
          const dx = bx - ax;
          if (Math.abs(dx) > wrapThreshold) {
            // Wrap crossing — split into two segments at the boundary
            if (dx > 0) {
              const wrappedDx = GAME_WIDTH - dx;
              const t = ax / wrappedDx;
              const boundaryY = ay + (by - ay) * t;
              this.trajectoryGfx.moveTo(ax, ay);
              this.trajectoryGfx.lineTo(0, boundaryY);
              this.trajectoryGfx.stroke({ width: 1, color: 0xff0000, alpha: 0.6 });
              this.trajectoryGfx.moveTo(GAME_WIDTH, boundaryY);
              this.trajectoryGfx.lineTo(bx, by);
              this.trajectoryGfx.stroke({ width: 1, color: 0xff0000, alpha: 0.6 });
            } else {
              const wrappedDx = GAME_WIDTH + dx;
              const t = (GAME_WIDTH - ax) / wrappedDx;
              const boundaryY = ay + (by - ay) * t;
              this.trajectoryGfx.moveTo(ax, ay);
              this.trajectoryGfx.lineTo(GAME_WIDTH, boundaryY);
              this.trajectoryGfx.stroke({ width: 1, color: 0xff0000, alpha: 0.6 });
              this.trajectoryGfx.moveTo(0, boundaryY);
              this.trajectoryGfx.lineTo(bx, by);
              this.trajectoryGfx.stroke({ width: 1, color: 0xff0000, alpha: 0.6 });
            }
          } else {
            this.trajectoryGfx.moveTo(ax, ay);
            this.trajectoryGfx.lineTo(bx, by);
            this.trajectoryGfx.stroke({ width: 1, color: 0xff0000, alpha: 0.6 });
          }
        }
      }
    }
    this.container.addChild(this.trajectoryGfx);

    // Render terrain segments as white lines
    const terrainGfx = new Graphics();
    const segsToRender = this.debugMode ? structuralSegments : levelData.segments;
    for (const seg of segsToRender) {
      terrainGfx.moveTo(seg.a.x, seg.a.y);
      terrainGfx.lineTo(seg.b.x, seg.b.y);
      terrainGfx.stroke({ width: 2, color: 0xffffff });
    }
    this.container.addChild(terrainGfx);

    // Debug graphics layer for incrementally-drawn connections
    this.debugGraphics = new Graphics();
    this.container.addChild(this.debugGraphics);

    // Create sprites
    this.tee = createTeeSprite(this.app.renderer);
    this.ball = createBallSprite(this.app.renderer);
    this.ballWrapX = createBallSprite(this.app.renderer);
    this.ballWrapX.visible = false;
    this.ballWrapY = createBallSprite(this.app.renderer);
    this.ballWrapY.visible = false;

    // Position tee on terrain surface
    const teeX = levelData.teeX;
    const teeSurfaceY = levelData.teeSurfaceY;
    this.teePos = vec2(teeX, teeSurfaceY - TEE_HEIGHT - BALL_RADIUS);
    this.tee.position.set(teeX, teeSurfaceY);
    this.ball.position.set(this.teePos.x, this.teePos.y);

    // Z-order: sky → cupFill → trajectory → structural → debug → tee → ball → wrapSprites → arrow → aimGraphics
    this.container.addChild(this.tee);
    this.container.addChild(this.ball);
    this.container.addChild(this.ballWrapX);
    this.container.addChild(this.ballWrapY);

    // Off-screen arrow indicator (small filled white triangle pointing down)
    this.offScreenArrow = new Graphics();
    this.offScreenArrow.moveTo(-6, 0);
    this.offScreenArrow.lineTo(6, 0);
    this.offScreenArrow.lineTo(0, 10);
    this.offScreenArrow.closePath();
    this.offScreenArrow.fill({ color: 0xffffff, alpha: 0.8 });
    this.offScreenArrow.visible = false;
    this.container.addChild(this.offScreenArrow);

    this.aimGraphics = new Graphics();
    this.container.addChild(this.aimGraphics);

    // Reset state
    this.ballState = null;
    this.phase = 'aiming';
    this.aimState = null;

    // Show status text
    if (this.debugMode) {
      const phase2 = levelData.attempts > 50;
      let attemptsLabel = phase2
        ? `Level ${seed} — ${levelData.attempts} attempts (phase 2)`
        : `Level ${seed} — ${levelData.attempts} attempt(s)`;
      if (this.retryCount > 0) {
        attemptsLabel += ` [retry ${this.retryCount}]`;
      }
      this.showStatusText(attemptsLabel, this.retryCount > 0 ? 0xff8800 : (phase2 ? 0xffaa00 : 0xffffff));
    }

    console.log(`Level ${seed} (retry ${this.retryCount}): tee=(${teeX}, ${teeSurfaceY.toFixed(0)}), hole=(${this.holeX.toFixed(0)}, ${this.holeSurfaceY.toFixed(0)}), edges=${levelData.numEdges}, connections=${this.totalConnections}, attempts=${levelData.attempts}`);
  }

  private onPointerDown(event: FederatedPointerEvent): void {
    if (this.phase !== 'aiming') return;
    const local = this.container.toLocal(event.global);
    this.aimState = beginAim(vec2(local.x, local.y));
  }

  private onPointerMove(event: FederatedPointerEvent): void {
    if (this.phase !== 'aiming') return;
    if (!this.aimState) return;
    const local = this.container.toLocal(event.global);
    this.aimState = updateAim(this.aimState, vec2(local.x, local.y));
  }

  private onPointerUp(): void {
    if (this.phase !== 'aiming') return;
    if (!this.aimState) return;

    if (!isInCancelZone(this.aimState)) {
      const vel = getLaunchVelocity(this.aimState);
      this.ballState = createBall(this.ball.x, this.ball.y, vel.x, vel.y);
      this.phase = 'flight';
      this.timeAccum = 0;
      this.strokes++;
      console.log(`Stroke ${this.strokes}: launch (${vel.x.toFixed(1)}, ${vel.y.toFixed(1)})`);
    }

    this.aimState = null;
    this.aimGraphics.clear();
  }

  update(deltaTime: number): void {
    switch (this.phase) {
      case 'aiming':
        this.updateAiming();
        break;
      case 'flight':
        this.updateFlight(deltaTime);
        break;
      case 'scored':
      case 'missed':
        this.updatePause(deltaTime);
        break;
    }
  }

  private updateAiming(): void {
    if (!this.aimState) return;

    this.aimGraphics.clear();

    const { touchOrigin, dragPos, power } = this.aimState;
    const inCancel = isInCancelZone(this.aimState);

    // 1. Cancel circle — hollow circle at touchOrigin
    this.aimGraphics.circle(touchOrigin.x, touchOrigin.y, CANCEL_RADIUS);
    this.aimGraphics.stroke({ width: 1, color: 0xffffff, alpha: 0.3 });

    if (inCancel) return;

    // Compute direction from touch origin toward drag
    const dx = dragPos.x - touchOrigin.x;
    const dy = dragPos.y - touchOrigin.y;
    const dragDist = Math.sqrt(dx * dx + dy * dy);
    const ux = dx / dragDist;
    const uy = dy / dragDist;

    // Clamp visual length to MAX_DRAG_DISTANCE
    const clampedDist = Math.min(dragDist, MAX_DRAG_DISTANCE);

    // Start lines at the cancel circle border, not the centre
    const dragStartX = touchOrigin.x + ux * CANCEL_RADIUS;
    const dragStartY = touchOrigin.y + uy * CANCEL_RADIUS;
    const arrowStartX = touchOrigin.x - ux * CANCEL_RADIUS;
    const arrowStartY = touchOrigin.y - uy * CANCEL_RADIUS;

    // 2. Drag line — dotted grey from circle border toward drag, capped at max
    const dragEndX = touchOrigin.x + ux * clampedDist;
    const dragEndY = touchOrigin.y + uy * clampedDist;
    this.drawDashedLine(dragStartX, dragStartY, dragEndX, dragEndY, 6, 4, 0x888888, 0.5);

    // 3. Force arrow — solid white from circle border in launch direction, capped at max
    const launchVel = getLaunchVelocity(this.aimState);
    if (power > 0) {
      const arrowEndX = touchOrigin.x - ux * clampedDist;
      const arrowEndY = touchOrigin.y - uy * clampedDist;

      // Arrow shaft
      this.aimGraphics.moveTo(arrowStartX, arrowStartY);
      this.aimGraphics.lineTo(arrowEndX, arrowEndY);
      this.aimGraphics.stroke({ width: 2, color: 0xffffff, alpha: 1 });

      // Arrowhead
      const headLen = 8;
      const angle = Math.atan2(arrowEndY - arrowStartY, arrowEndX - arrowStartX);
      this.aimGraphics.moveTo(
        arrowEndX - headLen * Math.cos(angle - 0.4),
        arrowEndY - headLen * Math.sin(angle - 0.4),
      );
      this.aimGraphics.lineTo(arrowEndX, arrowEndY);
      this.aimGraphics.lineTo(
        arrowEndX - headLen * Math.cos(angle + 0.4),
        arrowEndY - headLen * Math.sin(angle + 0.4),
      );
      this.aimGraphics.stroke({ width: 2, color: 0xffffff, alpha: 1 });
    }

    // 4. Trajectory preview — physics-simulated fading dots
    const ballState = createBall(this.ball.x, this.ball.y, launchVel.x, launchVel.y);
    const trajectory = simulate(ballState, this.terrain, TRAJECTORY_MAX_STEPS);
    for (let i = 0; i < trajectory.length; i++) {
      const p = trajectory[i]!;
      const alpha = 0.6 * (1 - i / trajectory.length);
      this.aimGraphics.circle(p.x, p.y, 2);
      this.aimGraphics.fill({ color: 0xffffff, alpha });
    }

    this.updateVisualWrap();
  }

  private updateFlight(deltaTime: number): void {
    if (!this.ballState) return;

    this.timeAccum += deltaTime;

    while (this.timeAccum >= PHYSICS.FIXED_DT) {
      step(this.ballState, this.terrain);
      this.timeAccum -= PHYSICS.FIXED_DT;

      // Update ball sprite each step for smooth rendering
      this.ball.position.set(this.ballState.pos.x, this.ballState.pos.y);

      // Compute ball speed
      const speed = Math.sqrt(lengthSq(this.ballState.vel));

      // Check if ball is in the hole and slow enough to capture
      if (isInHole(
        this.ballState.pos.x,
        this.ballState.pos.y,
        speed,
        this.holeX,
        this.holeSurfaceY,
        HOLE_WIDTH,
        this.holeDepth,
        HOLE_CAPTURE_SPEED
      )) {
        this.phase = 'scored';
        this.phaseTimer = SCORED_PAUSE;
        this.hideWrapVisuals();
        console.log(`Scored! Hole-in-one in ${this.strokes} stroke(s)`);
        return;
      }

      // Check at rest (miss — ball stopped outside hole)
      if (this.ballState.atRest) {
        this.phase = 'missed';
        this.phaseTimer = MISSED_PAUSE;
        this.hideWrapVisuals();
        console.log(`Missed — ball at rest. Strokes: ${this.strokes}`);
        return;
      }
    }

    // Check off-screen (miss)
    if (this.ballState.pos.y > GAME_HEIGHT + 100) {
      this.phase = 'missed';
      this.phaseTimer = MISSED_PAUSE;
      this.offScreenArrow.visible = false;
      console.log(`Missed — ball off screen. Strokes: ${this.strokes}`);
    }

    // Update ball sprite position after stepping
    this.ball.position.set(this.ballState.pos.x, this.ballState.pos.y);

    // Update visual wrapping
    this.updateVisualWrap();

    // Off-screen arrow when ball is above viewport (non-cave mode only)
    if (!this.terrain.verticalWrap && this.ballState.pos.y < -BALL_RADIUS) {
      this.offScreenArrow.position.set(this.ballState.pos.x, 16);
      this.offScreenArrow.visible = true;
    } else {
      this.offScreenArrow.visible = false;
    }
  }

  private updatePause(deltaTime: number): void {
    this.phaseTimer -= deltaTime;
    if (this.phaseTimer <= 0) {
      if (this.phase === 'scored') {
        this.currentSeed++;
        this.retryCount = 0;
        this.fixedTeePos = undefined;
        this.strokes = 0;
        this.loadLevel(this.currentSeed);
        console.log(`Loading level ${this.currentSeed}`);
        return;
      }
      this.resetToTee();
    }
  }

  private hideWrapVisuals(): void {
    this.ballWrapX.visible = false;
    this.ballWrapY.visible = false;
    this.offScreenArrow.visible = false;
  }

  private updateVisualWrap(): void {
    const bx = this.ball.x;
    const by = this.ball.y;

    // Horizontal wrap sprite
    if (bx < BALL_RADIUS) {
      this.ballWrapX.position.set(bx + GAME_WIDTH, by);
      this.ballWrapX.visible = true;
    } else if (bx > GAME_WIDTH - BALL_RADIUS) {
      this.ballWrapX.position.set(bx - GAME_WIDTH, by);
      this.ballWrapX.visible = true;
    } else {
      this.ballWrapX.visible = false;
    }

    // Vertical wrap sprite (cave mode only)
    if (this.terrain.verticalWrap) {
      if (by < BALL_RADIUS) {
        this.ballWrapY.position.set(bx, by + GAME_HEIGHT);
        this.ballWrapY.visible = true;
      } else if (by > GAME_HEIGHT - BALL_RADIUS) {
        this.ballWrapY.position.set(bx, by - GAME_HEIGHT);
        this.ballWrapY.visible = true;
      } else {
        this.ballWrapY.visible = false;
      }
    } else {
      this.ballWrapY.visible = false;
    }
  }

  /** Check if any segment in a connection group intersects the trajectory.
   *  Skips wrapped trajectory segments (where consecutive points jump across the screen).
   *  Also does proximity check — if a connecting segment passes within a few pixels
   *  of a trajectory point, it counts as an intersection. */
  private connectionIntersectsTrajectory(group: Segment[]): boolean {
    const wrapThreshold = GAME_WIDTH / 2;
    const PROXIMITY = 8; // pixels — catch near-misses from sparse trajectory sampling

    for (const connSeg of group) {
      for (let j = 0; j < this.trajectoryPoints.length - 1; j++) {
        const ta = this.trajectoryPoints[j]!;
        const tb = this.trajectoryPoints[j + 1]!;

        // Skip wrapped trajectory segments (phantom lines across the screen)
        if (Math.abs(tb.x - ta.x) > wrapThreshold) continue;

        // Exact segment intersection
        const trajSeg: Segment = { a: ta, b: tb };
        if (segmentsIntersect(connSeg, trajSeg)) return true;
      }

      // Proximity check: does any trajectory point lie close to this connecting segment?
      for (const tp of this.trajectoryPoints) {
        if (this.pointToSegmentDist(tp, connSeg) < PROXIMITY) return true;
      }
    }

    return false;
  }

  /** Minimum distance from a point to a line segment. */
  private pointToSegmentDist(p: Vec2, seg: Segment): number {
    const dx = seg.b.x - seg.a.x;
    const dy = seg.b.y - seg.a.y;
    const lenSq = dx * dx + dy * dy;
    if (lenSq === 0) {
      const ex = p.x - seg.a.x, ey = p.y - seg.a.y;
      return Math.sqrt(ex * ex + ey * ey);
    }
    let t = ((p.x - seg.a.x) * dx + (p.y - seg.a.y) * dy) / lenSq;
    t = Math.max(0, Math.min(1, t));
    const cx = seg.a.x + t * dx - p.x;
    const cy = seg.a.y + t * dy - p.y;
    return Math.sqrt(cx * cx + cy * cy);
  }

  /** After all connections are drawn, check if the only blue lines are cup-connected.
   *  If so, re-simulate with all connecting terrain included. If the ball still scores,
   *  redraw those blue lines as green. */
  private tryResimulateWithConnections(): void {
    const blueConns = this.drawnConnections.filter(c => c.isBlue);
    if (blueConns.length === 0) {
      this.levelPassed = true;
      console.log('All connections clear — no re-simulation needed');
      return;
    }
    const hasNonCupBlue = blueConns.some(c => !c.isCupConn);
    if (hasNonCupBlue) {
      this.levelPassed = false;
      this.showStatusText('FAILED — non-cup intersection', 0xff4444);
      console.log('Non-cup blue lines present — skipping re-simulation');
      return;
    }

    // Only cup-connected blue lines — re-simulate with full terrain
    this.showStatusText('Re-simulating with connecting terrain...', 0xffff00);

    const allConnSegs: Segment[] = [];
    for (const conn of this.drawnConnections) {
      allConnSegs.push(...conn.segs);
    }
    const fullTerrain = new SegmentTerrain([...this.structuralSegments, ...allConnSegs]);
    const ballStartY = this.teePos.y;
    const ball = createBall(this.teePos.x, ballStartY, this.solutionVx, this.solutionVy);

    let scored = false;
    for (let i = 0; i < 20000; i++) {
      step(ball, fullTerrain);
      const speed = Math.sqrt(lengthSq(ball.vel));
      if (isInHole(ball.pos.x, ball.pos.y, speed, this.holeX, this.holeSurfaceY, HOLE_WIDTH, this.holeDepth, HOLE_CAPTURE_SPEED)) {
        scored = true;
        break;
      }
      if (ball.atRest) break;
      if (ball.pos.y > GAME_HEIGHT + 200) break;
    }

    if (scored) {
      // Redraw the blue cup connections as green
      for (const conn of this.drawnConnections) {
        if (conn.isBlue) {
          this.drawConnectionSegments(conn.segs, 0x00ff00);
          conn.isBlue = false;
        }
      }
      this.levelPassed = true;
      this.showStatusText('Re-simulation PASSED — ball still scores', 0x00ff00);
      console.log('Re-simulation: ball still scores — blue cup lines redrawn as green');
    } else {
      this.levelPassed = false;
      this.showStatusText('Re-simulation FAILED — ball does not score', 0xff4444);
      console.log('Re-simulation: ball does NOT score — blue lines remain');
    }
  }

  /** Display a status message on screen. Replaces any previous status text. */
  private showStatusText(message: string, color: number): void {
    // Remove previous status text if any
    const existing = this.container.getChildByLabel('statusText');
    if (existing) this.container.removeChild(existing);

    const text = new Text({
      text: message,
      style: { fontFamily: 'monospace', fontSize: 24, fill: color },
    });
    text.label = 'statusText';
    text.anchor.set(0.5, 0);
    text.position.set(GAME_WIDTH / 2, 20);
    this.container.addChild(text);
  }

  /** Build segments for a straight-line connection, splitting at wrap boundary if needed. */
  private makeConnectionSegments(from: Vec2, to: Vec2, wrap: boolean): Segment[] {
    if (!wrap) {
      return [{ a: from, b: to }];
    }
    // Wrap: go rightward off-screen, re-enter from left
    const rightDist = GAME_WIDTH - from.x;
    const leftDist = to.x;
    const totalDist = rightDist + leftDist;
    const t = totalDist > 0 ? rightDist / totalDist : 0.5;
    const boundaryY = from.y + t * (to.y - from.y);
    return [
      { a: from, b: vec2(GAME_WIDTH, boundaryY) },
      { a: vec2(0, boundaryY), b: to },
    ];
  }

  /** Draw connection segments onto the debug graphics layer. */
  private drawConnectionSegments(segs: Segment[], color: number): void {
    for (const seg of segs) {
      this.debugGraphics.moveTo(seg.a.x, seg.a.y);
      this.debugGraphics.lineTo(seg.b.x, seg.b.y);
      this.debugGraphics.stroke({ width: 2, color });
    }
  }

  private resetToTee(): void {
    this.ball.position.set(this.teePos.x, this.teePos.y);
    this.ballState = null;
    this.phase = 'aiming';
    this.ballWrapX.visible = false;
    this.ballWrapY.visible = false;
    this.offScreenArrow.visible = false;
  }

  private drawDashedLine(
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    dashLen: number,
    gapLen: number,
    color: number,
    alpha: number,
  ): void {
    const dx = x2 - x1;
    const dy = y2 - y1;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist === 0) return;

    const ux = dx / dist;
    const uy = dy / dist;
    let drawn = 0;

    while (drawn < dist) {
      const segEnd = Math.min(drawn + dashLen, dist);
      this.aimGraphics.moveTo(x1 + ux * drawn, y1 + uy * drawn);
      this.aimGraphics.lineTo(x1 + ux * segEnd, y1 + uy * segEnd);
      this.aimGraphics.stroke({ width: 1, color, alpha });
      drawn = segEnd + gapLen;
    }
  }

  enter(): void {
    // nothing to set up yet
  }

  exit(): void {
    window.removeEventListener('keydown', this.onKeyDown);
  }
}
